import type { Condition } from './workflow.js';

export type ConditionFields = Readonly<Record<string, unknown>>;

function isEmpty(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.trim() === '') ||
    (Array.isArray(value) && value.length === 0)
  );
}

function compare(a: unknown, b: unknown): number | null {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  const na = typeof a === 'string' ? Number(a) : a;
  const nb = typeof b === 'string' ? Number(b) : b;
  if (typeof na === 'number' && typeof nb === 'number' && !Number.isNaN(na) && !Number.isNaN(nb)) {
    return na - nb;
  }
  if (typeof a === 'string' && typeof b === 'string') return a.localeCompare(b);
  return null;
}

/** Вычисляет условие (маршрута, шаблона, видимости поля) по полям заявки. Несравнимые значения дают false. */
export function evaluateCondition(condition: Condition | undefined, fields: ConditionFields): boolean {
  if (!condition) return true;
  if ('all' in condition) return condition.all.every((c) => evaluateCondition(c, fields));
  if ('any' in condition) return condition.any.some((c) => evaluateCondition(c, fields));
  if ('not' in condition) return !evaluateCondition(condition.not, fields);

  const actual = fields[condition.field];
  const expected = condition.value;
  switch (condition.op) {
    case 'empty':
      return isEmpty(actual);
    case 'not_empty':
      return !isEmpty(actual);
    case 'eq':
      return actual === expected || compare(actual, expected) === 0;
    case 'neq':
      return !(actual === expected || compare(actual, expected) === 0);
    case 'in':
      return Array.isArray(expected) && expected.some((v) => v === actual || compare(actual, v) === 0);
    case 'not_in':
      return !(Array.isArray(expected) && expected.some((v) => v === actual || compare(actual, v) === 0));
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      const diff = compare(actual, expected);
      if (diff === null) return false;
      if (condition.op === 'gt') return diff > 0;
      if (condition.op === 'gte') return diff >= 0;
      if (condition.op === 'lt') return diff < 0;
      return diff <= 0;
    }
  }
}

/** Поля, на которые ссылается условие. */
export function conditionFields(condition: Condition | undefined): string[] {
  if (!condition) return [];
  if ('all' in condition) return condition.all.flatMap(conditionFields);
  if ('any' in condition) return condition.any.flatMap(conditionFields);
  if ('not' in condition) return conditionFields(condition.not);
  return [condition.field];
}
